import { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '@/contexts/AuthContext';
import AppShell from '@/components/ops/AppShell';
import LeadDetailModal from '@/components/LeadDetailModal';
import { Users2, Calendar, AlertCircle, ArrowRight } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const getAuth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('auth_token')}` } });

function StatTile({ label, value, sub, icon: Icon, testid }) {
  return (
    <div className="bg-white border border-slate-200 rounded-lg px-4 py-3.5" data-testid={testid}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="text-[11px] uppercase tracking-wider font-medium text-slate-500 leading-tight">{label}</p>
          <p className="text-[22px] font-semibold text-slate-900 leading-tight mt-1.5">{value}</p>
          {sub && <p className="text-[11px] text-slate-500 mt-1 leading-snug">{sub}</p>}
        </div>
        {Icon && (
          <div className="w-7 h-7 rounded-md bg-slate-50 border border-slate-200 grid place-items-center shrink-0">
            <Icon size={13} className="text-slate-500" />
          </div>
        )}
      </div>
    </div>
  );
}

function PanelCard({ title, subtitle, action, children, testid }) {
  return (
    <div className="bg-white border border-slate-200 rounded-lg" data-testid={testid}>
      <header className="flex items-center justify-between px-4 py-3 border-b border-slate-200">
        <div>
          <h3 className="text-[13px] font-semibold text-slate-900 leading-tight">{title}</h3>
          {subtitle && <p className="text-[11px] text-slate-500 leading-tight mt-0.5">{subtitle}</p>}
        </div>
        {action}
      </header>
      <div className="px-1.5 py-1.5">{children}</div>
    </div>
  );
}

const fmtRel = (s) => {
  if (!s) return '—';
  const diff = Date.now() - new Date(s).getTime();
  const h = 36e5, d = 24 * h;
  if (diff < h) return `${Math.max(1, Math.floor(diff / 60_000))}m ago`;
  if (diff < d) return `${Math.floor(diff / h)}h ago`;
  if (diff < 30 * d) return `${Math.floor(diff / d)}d ago`;
  return new Date(s).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
};
const fmtDate = (s) => s ? new Date(s).toLocaleDateString(undefined, { month: 'short', day: 'numeric' }) : '—';

export default function OpsInterviewerDashboard() {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedLead, setSelectedLead] = useState(null);

  useEffect(() => {
    if (authLoading) return;
    if (!user) { navigate('/ops/login'); return; }
    if (!user.roles?.includes('interviewer') && !user.roles?.includes('admin')) { navigate('/ops/login'); return; }
  }, [user, authLoading, navigate]);

  const fetchData = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/ops/dashboards/interviewer`, getAuth());
      setData(data);
    } catch {}
    finally { setLoading(false); }
  }, []);
  useEffect(() => { if (user) fetchData(); }, [user, fetchData]);

  if (authLoading || loading || !data) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FAFBFC]">
        <div className="w-7 h-7 border-2 border-slate-300 border-t-slate-900 rounded-full animate-spin" />
      </div>
    );
  }

  const t = data.tiles;

  return (
    <AppShell pageTitle="Dashboard" subtitle="Your pipeline at a glance" activeNav="dashboard" onNavigate={() => {}} role="interviewer">
      <div className="px-6 py-5 space-y-4" data-testid="interviewer-dashboard-page">

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3" data-testid="interviewer-stat-tiles">
          <StatTile testid="tile-my-active-leads"      label="My Active Leads"        value={t.my_active_leads.count} icon={Users2} />
          <StatTile testid="tile-leads-pending-action" label="Leads Pending Action"   value={t.leads_pending_action.count} sub="Awaiting your next step" icon={AlertCircle} />
          <StatTile testid="tile-upcoming-interviews"  label="Upcoming Interviews"    value={t.upcoming_interviews.count} sub={`Next ${t.upcoming_interviews.window_days} days`} icon={Calendar} />
        </div>

        <PanelCard
          title="Needs your attention"
          subtitle="Leads waiting on your next step, oldest first"
          testid="needs-attention-card"
          action={
            <button onClick={() => navigate('/ops/interviewer/pipeline')} className="text-[11px] text-slate-500 hover:text-slate-900 inline-flex items-center gap-1" data-testid="needs-view-pipeline">
              Open Kanban <ArrowRight size={11} />
            </button>
          }
        >
          {data.needs_attention.length === 0 ? (
            <p className="text-[12px] text-slate-500 px-3 py-6 text-center">All clear. Nothing waiting on you.</p>
          ) : (
            <ul className="divide-y divide-slate-100">
              {data.needs_attention.map(n => (
                <li key={n.id} data-testid={`needs-row-${n.id}`}>
                  <button onClick={() => setSelectedLead(n.id)} className="w-full flex items-center justify-between gap-3 px-2.5 py-2 hover:bg-slate-50/60 rounded-md text-left">
                    <div className="min-w-0">
                      <p className="text-[13px] text-slate-900 truncate leading-tight">{n.client_name}</p>
                      <p className="text-[11px] text-slate-500 mt-0.5">{n.stage} · {n.days_since_activity}d since last activity</p>
                    </div>
                    <span className="text-[11px] text-slate-400 shrink-0">{fmtRel(n.updated_at)}</span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </PanelCard>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
          <PanelCard title="Upcoming this week" subtitle="Scheduled interviews (next 14 days)" testid="upcoming-card">
            {data.upcoming_this_week.length === 0 ? (
              <p className="text-[12px] text-slate-500 px-3 py-6 text-center">Nothing scheduled.</p>
            ) : (
              <ul className="divide-y divide-slate-100">
                {data.upcoming_this_week.map(u => (
                  <li key={u.id} data-testid={`upcoming-row-${u.id}`}>
                    <button onClick={() => setSelectedLead(u.id)} className="w-full flex items-center justify-between gap-3 px-2.5 py-2 hover:bg-slate-50/60 rounded-md text-left">
                      <div className="min-w-0">
                        <p className="text-[13px] text-slate-900 truncate leading-tight">{u.client_name} <span className="text-slate-400">— {u.loved_one_first_name}</span></p>
                        <p className="text-[11px] text-slate-500 mt-0.5">{fmtDate(u.interview_date)}{u.interview_time ? ` · ${u.interview_time}` : ''}{u.interview_location ? ` · ${u.interview_location}` : ''}</p>
                      </div>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </PanelCard>

          <PanelCard title="Recent client activity" subtitle="System events on your leads" testid="client-activity-card">
            {data.recent_activity.length === 0 ? (
              <p className="text-[12px] text-slate-500 px-3 py-6 text-center">Quiet right now.</p>
            ) : (
              <ul className="divide-y divide-slate-100">
                {data.recent_activity.map((a, i) => (
                  <li key={i} className="px-2.5 py-2" data-testid={`activity-row-${i}`}>
                    <p className="text-[13px] text-slate-900 leading-tight truncate">{a.text}</p>
                    <p className="text-[11px] text-slate-500 mt-0.5">{fmtRel(a.created_at)}</p>
                  </li>
                ))}
              </ul>
            )}
          </PanelCard>
        </div>
      </div>
      {selectedLead && (
        <LeadDetailModal
          leadId={selectedLead}
          stages={[]}
          users={[]}
          isAdmin={false}
          onClose={() => { setSelectedLead(null); fetchData(); }}
          onUpdated={fetchData}
        />
      )}
    </AppShell>
  );
}
